import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject, getBlob } from 'firebase/storage';
import { db, storage } from './firebase';
import CustomImageCropper from './CustomImageCropper';
import './SimpleProfileEditor.css';

const SimpleProfileEditor = ({ dancerName, onSuccess, canEditSocialMedia = true, currentAgeCategory = null }) => {
  const [profileData, setProfileData] = useState({
    bio: '',
    instagram: '',
    tiktok: ''
  });
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [currentProfilePictureUrl, setCurrentProfilePictureUrl] = useState(null);
  const [currentProfilePicturePath, setCurrentProfilePicturePath] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Image editor state
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [originalImageSrc, setOriginalImageSrc] = useState(null);
  const [isEditingExisting, setIsEditingExisting] = useState(false);

  useEffect(() => {
    if (dancerName) {
      loadProfileData();
    }
  }, [dancerName]);

  const loadProfileData = async () => {
    setLoading(true);
    try {
      // First, load from dancerProfiles collection (primary source for profile pictures)
      const profileRef = doc(db, 'dancerProfiles', dancerName);
      const profileSnap = await getDoc(profileRef);
      
      let profilePictureData = null;
      
      if (profileSnap.exists()) {
        const profileData = profileSnap.data();
        
        // Load profile picture from dancerProfiles if it exists
        if (profileData.profilePicture) {
          // Check if it's already a full URL or a storage path
          if (profileData.profilePicture.startsWith('https://')) {
            // It's already a full URL
            setCurrentProfilePictureUrl(profileData.profilePicture);
            setCurrentProfilePicturePath(profileData.profilePicture); // Store the URL as path for now
          } else {
            // It's a storage path, get download URL
            setCurrentProfilePicturePath(profileData.profilePicture);
            try {
              const pictureRef = ref(storage, profileData.profilePicture);
              const pictureUrl = await getDownloadURL(pictureRef);
              setCurrentProfilePictureUrl(pictureUrl);
            } catch (error) {
              setCurrentProfilePictureUrl(null);
            }
          }
          profilePictureData = profileData.profilePicture;
        }
        
        // Load other profile data from dancerProfiles
        const profileDataFromProfiles = {
          bio: profileData.bio || '',
          instagram: profileData.instagram || '',
          tiktok: profileData.tiktok || ''
        };
        
        // We'll merge with dancers collection data later
        setProfileData(profileDataFromProfiles);
      }
      
      // Also check dancers collection for additional data and fallback profile picture
      const dancerRef = doc(db, 'dancers', dancerName);
      const dancerSnap = await getDoc(dancerRef);
      
      if (dancerSnap.exists()) {
        const dancerData = dancerSnap.data();
        
        // Merge social media data from both collections (dancers collection as fallback)
        setProfileData(prev => ({
          bio: prev.bio || dancerData.bio || '',
          instagram: prev.instagram || dancerData.socialMedia?.instagram || '',
          tiktok: prev.tiktok || dancerData.socialMedia?.tiktok || ''
        }));
        
        // Use profile picture from dancers collection only if we don't have one from dancerProfiles
        if (!profilePictureData && dancerData.profilePicture) {
          setCurrentProfilePicturePath(dancerData.profilePicture);
          try {
            const pictureRef = ref(storage, dancerData.profilePicture);
            const pictureUrl = await getDownloadURL(pictureRef);
            setCurrentProfilePictureUrl(pictureUrl);
          } catch (error) {
            setCurrentProfilePictureUrl(null);
          }
        }
      } else {
        // If dancers collection doesn't exist either, ensure we have empty state
        if (!profileSnap.exists()) {
          setProfileData({
            bio: '',
            instagram: '',
            tiktok: ''
          });
        }
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
      // Set empty state on error
      setProfileData({
        bio: '',
        instagram: '',
        tiktok: ''
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Block social media changes if not allowed
    if ((name === 'instagram' || name === 'tiktok') && !canEditSocialMedia) {
      return;
    }
    
    // Handle bio character limit
    if (name === 'bio' && value.length > 300) {
      return;
    }

    // Clean Instagram and TikTok usernames (remove @ if present)
    if (name === 'instagram' || name === 'tiktok') {
      const cleanValue = value.replace(/^@/, '');
      setProfileData(prev => ({
        ...prev,
        [name]: cleanValue
      }));
    } else {
      setProfileData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };



  const handlePictureSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file.');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Please select an image smaller than 5MB.');
        return;
      }

      // Simple and fast - just create URL and show editor
      const imageUrl = URL.createObjectURL(file);
      setOriginalImageSrc(imageUrl);
      setIsEditingExisting(false);
      setShowImageEditor(true);
    }
  };

  const handleEditExistingPicture = async () => {
    if (currentProfilePicturePath) {
      try {
        // Use Firebase Storage SDK to get blob directly (bypasses CORS)
        setLoading(true);
        const storageRef = ref(storage, currentProfilePicturePath);
        const blob = await getBlob(storageRef);
        const blobUrl = URL.createObjectURL(blob);
        
        setOriginalImageSrc(blobUrl);
        setIsEditingExisting(true);
        setShowImageEditor(true);
        setLoading(false);
      } catch (error) {
        console.error('Error loading existing image:', error);
        alert('Failed to load existing image. Please try uploading a new one.');
        setLoading(false);
      }
    }
  };

  const handleCancelEdit = () => {
    // Clean up object URL if it exists
    if (originalImageSrc && originalImageSrc.startsWith('blob:')) {
      URL.revokeObjectURL(originalImageSrc);
    }
    
    setShowImageEditor(false);
    setOriginalImageSrc(null);
    setIsEditingExisting(false);
    
    // If we were editing a new upload (not existing picture), clear the preview and file
    if (!isEditingExisting) {
      setProfilePicture(null);
      setProfilePicturePreview(null);
      // Reset file input
      const fileInput = document.getElementById('profilePicture');
      if (fileInput) fileInput.value = '';
    }
  };

  const handleApplyCrop = (croppedImageBlob) => {
    try {
      if (!croppedImageBlob) {
        alert('Failed to crop image. Please try again.');
        return;
      }

      const croppedImageUrl = URL.createObjectURL(croppedImageBlob);
      setProfilePicturePreview(croppedImageUrl);

      // Convert blob to file
      const fileName = isEditingExisting 
        ? `${dancerName.replace(/\s+/g, '_')}_profile_edited_${new Date().toISOString().replace(/[:.]/g, '-')}.jpeg`
        : `${dancerName.replace(/\s+/g, '_')}_profile_${new Date().toISOString().replace(/[:.]/g, '-')}.jpeg`;
      
      const file = new File([croppedImageBlob], fileName, { type: 'image/jpeg' });
      setProfilePicture(file);

      // Clean up object URL if it exists
      if (originalImageSrc && originalImageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(originalImageSrc);
      }

      // Reset all editor state
      setShowImageEditor(false);
      setOriginalImageSrc(null);
      setIsEditingExisting(false);
    } catch (error) {
      console.error('Error cropping image:', error);
      alert('Failed to crop image. Please try again.');
    }
  };

  const handleDeletePicture = async () => {
    if (!currentProfilePicturePath) return;
    
    if (!window.confirm('Are you sure you want to delete your profile picture?')) {
      return;
    }

    try {
      setUploading(true);
      
      // Delete from Firebase Storage
      const pictureRef = ref(storage, currentProfilePicturePath);
      await deleteObject(pictureRef);
      
      // Update both collections to remove profile picture reference
      const dancerRef = doc(db, 'dancers', dancerName);
      const profileRef = doc(db, 'dancerProfiles', dancerName);
      
      const updateData = {
        profilePicture: null,
        lastUpdated: new Date()
      };
      
      await Promise.all([
        updateDoc(dancerRef, updateData),
        updateDoc(profileRef, updateData)
      ]);
      
      setCurrentProfilePicturePath(null);
      setCurrentProfilePictureUrl(null);
      setProfilePicturePreview(null);
      alert('Profile picture deleted successfully!');
      
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error deleting profile picture:', error);
      alert('Failed to delete profile picture. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const uploadProfilePicture = async () => {
    if (!profilePicture) return null;
    
    const sanitizedDancerName = dancerName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const fileExtension = profilePicture.name.split('.').pop();
    const fileName = `${sanitizedDancerName}_profile_${timestamp}.${fileExtension}`;
    
    const storageRef = ref(storage, `profile-pictures/${sanitizedDancerName}/${fileName}`);
    const uploadTask = uploadBytesResumable(storageRef, profilePicture);
    
    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve({
              url: downloadURL,
              path: `profile-pictures/${sanitizedDancerName}/${fileName}`
            });
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setUploading(!!profilePicture);
    
    try {
      let profilePictureData = currentProfilePicturePath;
      
      // Upload new profile picture if selected
      if (profilePicture) {
        const uploadResult = await uploadProfilePicture();
        profilePictureData = uploadResult.path;
        
        // Delete old profile picture if it exists
        if (currentProfilePicturePath) {
          try {
            const oldPictureRef = ref(storage, currentProfilePicturePath);
            await deleteObject(oldPictureRef);
          } catch (error) {
            // Silently ignore deletion errors
          }
        }
      }
      
      // Update both collections for consistency
      const dancerRef = doc(db, 'dancers', dancerName);
      const profileRef = doc(db, 'dancerProfiles', dancerName);
      
      const updateData = {
        bio: profileData.bio,
        socialMedia: {
          instagram: profileData.instagram,
          tiktok: profileData.tiktok
        },
        lastUpdated: new Date()
      };
      
      const profileUpdateData = {
        bio: profileData.bio,
        instagram: profileData.instagram,
        tiktok: profileData.tiktok,
        lastUpdated: new Date()
      };
      
      if (profilePictureData) {
        updateData.profilePicture = profilePictureData;
        profileUpdateData.profilePicture = profilePictureData;
      }
      
      // Update dancers collection
      await updateDoc(dancerRef, updateData);
      
      // Update or create dancerProfiles collection document
      try {
        await updateDoc(profileRef, profileUpdateData);
      } catch (error) {
        // If document doesn't exist, create it
        if (error.code === 'not-found') {
          await setDoc(profileRef, {
            ...profileUpdateData,
            profileClaimed: true,
            claimedAt: new Date()
          });
        } else {
          throw error;
        }
      }
      alert('Profile updated successfully!');
      setProfilePicture(null);
      setProfilePicturePreview(null);
      setCurrentProfilePicturePath(profilePictureData);
      
      // Reload the new picture URL
      if (profilePictureData) {
        try {
          const newPictureRef = ref(storage, profilePictureData);
          const newPictureUrl = await getDownloadURL(newPictureRef);
          setCurrentProfilePictureUrl(newPictureUrl);
        } catch (error) {
          setCurrentProfilePictureUrl(null);
        }
      } else {
        setCurrentProfilePictureUrl(null);
      }
      
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (loading) {
    return <div className="simple-profile-editor loading">Loading profile data...</div>;
  }

  return (
    <div className="simple-profile-editor">
      {/* Custom Image Editor Modal */}
      {showImageEditor && originalImageSrc && (
        <div className="image-editor-overlay">
          <div className="image-editor-modal">
            <div className="image-editor-header">
              <h3>Edit Profile Picture</h3>
              <button 
                type="button" 
                className="close-btn" 
                onClick={handleCancelEdit}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <CustomImageCropper 
              imageSrc={originalImageSrc}
              onCrop={handleApplyCrop}
              onCancel={handleCancelEdit}
            />
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="profile-form">
        
        {/* Profile Picture Section */}
        <div className="form-section">
          <h3>Profile Picture</h3>
          <div className="profile-picture-section">
            <div className="picture-preview">
              {profilePicturePreview ? (
                <img src={profilePicturePreview} alt="Profile preview" className="preview-image" />
              ) : currentProfilePictureUrl ? (
                <img 
                  src={currentProfilePictureUrl}
                  alt="Current profile" 
                  className="preview-image"
                />
              ) : (
                <div className="no-picture">No profile picture</div>
              )}
            </div>
            
            <div className="picture-controls">
              <input
                type="file"
                id="profilePicture"
                accept="image/*"
                onChange={handlePictureSelect}
                style={{ display: 'none' }}
              />
              <label htmlFor="profilePicture" className="upload-btn">
                📷 {currentProfilePictureUrl || profilePicturePreview ? 'Change Picture' : 'Upload Picture'}
              </label>
              
              {(currentProfilePictureUrl || profilePicturePreview) && (
                <>
                  <button
                    type="button"
                    onClick={profilePicturePreview ? handleEditExistingPicture : handleEditExistingPicture}
                    className="edit-btn"
                    disabled={uploading}
                  >
                    ✏️ Edit Picture
                  </button>
                  <button
                    type="button"
                    onClick={profilePicturePreview ? () => {
                      setProfilePicture(null);
                      setProfilePicturePreview(null);
                    } : handleDeletePicture}
                    className="delete-btn"
                    disabled={uploading}
                  >
                    🗑️ {profilePicturePreview ? 'Cancel' : 'Delete Picture'}
                  </button>
                </>
              )}
            </div>
            
            {uploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{Math.round(uploadProgress)}%</span>
              </div>
            )}
          </div>
        </div>

        {/* Instagram Username */}
        <div className="form-section">
          <label htmlFor="instagram">Instagram Username</label>
          {canEditSocialMedia ? (
            <div className="input-wrapper">
              <span className="input-prefix">@</span>
              <input
                type="text"
                id="instagram"
                name="instagram"
                value={profileData.instagram}
                onChange={handleInputChange}
                placeholder="username"
                className="username-input"
              />
            </div>
          ) : (
            <div className="social-field-locked">
              <div className="locked-input">
                <span className="input-prefix">@</span>
                <input
                  type="text"
                  value={profileData.instagram || 'Not available'}
                  disabled
                  className="username-input locked"
                  placeholder="Locked until Junior II"
                />
                <span className="lock-icon">🔒</span>
              </div>
              <p className="lock-message">
                Social media unlocks when you reach <strong>Junior II category and older</strong>. 
                Current: <strong>{currentAgeCategory || 'Unknown'}</strong>
              </p>
            </div>
          )}
        </div>

        {/* TikTok Username */}
        <div className="form-section">
          <label htmlFor="tiktok">TikTok Username</label>
          {canEditSocialMedia ? (
            <div className="input-wrapper">
              <span className="input-prefix">@</span>
              <input
                type="text"
                id="tiktok"
                name="tiktok"
                value={profileData.tiktok}
                onChange={handleInputChange}
                placeholder="username"
                className="username-input"
              />
            </div>
          ) : (
            <div className="social-field-locked">
              <div className="locked-input">
                <span className="input-prefix">@</span>
                <input
                  type="text"
                  value={profileData.tiktok || 'Not available'}
                  disabled
                  className="username-input locked"
                  placeholder="Locked until Junior II"
                />
                <span className="lock-icon">🔒</span>
              </div>
              <p className="lock-message">
                Social media unlocks when you reach <strong>Junior II category and older</strong>. 
                Current: <strong>{currentAgeCategory || 'Unknown'}</strong>
              </p>
            </div>
          )}
        </div>

        {/* Bio */}
        <div className="form-section">
          <label htmlFor="bio">
            Bio ({profileData.bio.length}/300 characters)
          </label>
          <textarea
            id="bio"
            name="bio"
            value={profileData.bio}
            onChange={handleInputChange}
            rows="4"
            placeholder="Tell us about yourself..."
            className="bio-textarea"
          />
        </div>

        {/* Submit Button */}
        <div className="form-actions">
          <button type="submit" disabled={saving || uploading} className="save-btn">
            {saving ? 'Saving...' : uploading ? 'Uploading...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SimpleProfileEditor;
